import type { ChannelSlice } from '../ChannelSlice';
import type { ColumnOptionsSlice } from '../ColumnOptionsSlice';
import type { FastIdeasSelector } from '../FastIdeasSelector';
import type { ResultsAreaSlice } from '../ResultsAreaSlice';
import type { SlowIdeasSelector } from '../SlowIdeasSelector';
import type { StickyFilterSlice } from '../StickyFilterSlice';
import type { WidgetTabSlice } from '../WidgetTabSlice';
import { Instant } from '@js-joda/core';
import { signal, computed, effect, untracked } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { doesAccessGrantAllowRequest } from '@thinkalpha/common/util/permissions.js';
import type { ConcreteIdea, Idea, NewIdea, ScannerPlan } from '@thinkalpha/platform-ws-client/contracts/ideas/index.js';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import { t } from 'i18next';
import { optional } from 'inversify';
import { IdeaDTO } from 'src/dtos/Idea';
import type { GridSettingsNavigationTarget } from 'src/features/grid-settings/models/NavigationModel';
import { ReactiveInjectable, reacts, inject, injectable } from 'src/features/ioc';
import { ScannerPlanDTO } from 'src/features/scanner/dtos/ScannerPlanDTO';
import { SCRANNER_CHANGE_EVENT } from 'src/features/scranner/dtos/ScrannerPlanDTO';
import { getValidationColumnDisplayName } from 'src/features/table/lib/validationColumns';
import type { TableStatus } from 'src/features/table/types';
import { useWidgetTabIdForUserViewKey } from 'src/lib/featureFlags/flags/useWidgetTabIdForUserViewKey';
import type { ScannerWidgetAdapter } from 'src/models/ScannerWidgetAdapter';
import type { ScannerWidgetModel } from 'src/models/ScannerWidgetModel';
import type { TableConstructionProgress } from 'src/models/TableModel';
import { makeHeaderElements } from 'src/models/WidgetModel';
import type { WidgetHeaderElement } from 'src/models/WidgetModel';
import { COMMON_MENU_HEADER_ELEMENTS, STANDALONE_COMMON_MENU_HEADER_ELEMENTS } from 'src/models/WidgetModel/impl';
// TODO make a mutation in src/queries/ideas to avoid this
// eslint-disable-next-line no-restricted-imports -- legacy direct import
import { createIdea, updateIdea } from 'src/queries/http/ideas';
import { getIdeaByIdQuery, invalidateIdeaRecordsQueries } from 'src/queries/ideas';
import { getTotalRowsCountQuery } from 'src/queries/table';
import { getWorkspaceConfigQuery } from 'src/queries/workspace';
import { generateWorkableColumnTemplateGridSettings } from 'src/routes/dashboard/widget-container/widget-settings-editor/context';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import { updateWidget } from 'src/store/actions/widgetAndChannel';
import type { ScannerWidgetViewModel } from 'src/store/types';
import type { ReactBindings } from 'src/types/bindings';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';
import { v4 } from 'uuid';

@injectable()
export class ScannerWidgetModelImpl extends ReactiveInjectable implements ScannerWidgetModel {
    #channelSlice: ChannelSlice;
    #widgetTabSlice: WidgetTabSlice;
    #fastIdeasSelector: FastIdeasSelector;
    #slowIdeasSelector: SlowIdeasSelector;
    #stickyFilterSlice: StickyFilterSlice;
    #columnOptionsSlice: ColumnOptionsSlice;

    #name: string;
    #description: string;
    #isDirty = signal<boolean>(false);
    #sectionVisibility = signal<{ builder: boolean; results: boolean }>({ builder: true, results: true });
    #planListenerCleanup: (() => void) | null = null;
    #log: ReturnType<ReactBindings['Logger']['getSubLogger']>;
    // Signal for widget name (overrides WidgetTabSlice's name to show idea name)
    #widgetName: Signal<string> = signal('');

    // Column template state signals
    #selectedColumnTemplate: Signal<ColumnTemplate | string | null> = signal(null);
    #isGridSettingsDialogOpen: Signal<boolean> = signal(false);
    #gridSettingsDialogTarget: Signal<GridSettingsNavigationTarget | null> = signal(null);
    #isColumnTemplateLibraryOpen: Signal<boolean> = signal(false);

    // Validation state signal
    #isValidationModeEnabled: Signal<boolean> = signal(false);
    #userViewKey: Signal<string | undefined> = signal(undefined);

    #connectionStatus: Signal<{
        status: TableStatus;
        progress: TableConstructionProgress;
        failureCount: number;
    }> = signal({
        status: 'uninitialized' as TableStatus,
        progress: undefined,
        failureCount: 0,
    });

    #tableRowCount: Signal<number> = signal(0);
    #refetchHandler: (() => void) | null = null;

    #rowCountTotalSignal: Signal<number> = signal(0);

    headerElements!: WidgetHeaderElement[];

    resultsAreaHeaderElements: WidgetHeaderElement[] = makeHeaderElements<ScannerWidgetModel['eventBus']>([
        { type: 'overflowDropdown', position: 0, alwaysVisible: true },
        { type: 'scrollingTableIndicator', position: 0.5, alwaysVisible: true },
        { type: 'stickyFilter', position: 1, priority: 80 },
        // Collapses after the widget's own controls (AC-FILTER-006).
        { type: 'columnFilters', position: 1.5, priority: 90 },
        { type: 'spacer', position: 3, alwaysVisible: true },
        {
            type: 'ideasMenu',
            position: 5,
            priority: 70,
            ideaType: 'slow',
            ideaPlanTypes: ['watchlist', 'screener', 'universe'],
            selectType: 'single',
            showSelected: false,
            allowDelete: true,
        },
        { type: 'columnTemplateMenu', position: 6, priority: 10, columnTemplateKey: 'ticker' },
        { type: 'settingsMenuNoScrolling', position: 7, priority: 60 },
        { type: 'tableRowCount', position: 9, priority: 30 },
        { type: 'connectionStatus', position: 10, priority: 40 },
        { type: 'exportCSVButton', position: 11, priority: 20 },
    ]);

    get canSaveIdea() {
        if (!this.#isDirty.value) {
            return false;
        }

        if ('id' in this.idea && 'permissions' in this.idea) {
            return doesAccessGrantAllowRequest(
                (this.idea as ConcreteIdea).permissions.effectiveDirectAccess || undefined,
                'writer',
            );
        }

        return false;
    }

    get columnTemplate(): ReadonlySignal<ColumnTemplate | string | null> {
        return this.#selectedColumnTemplate;
    }

    createNewScanner(): void {
        this.#lastSubmit.value = Instant.now();
        this.#plan = new ScannerPlanDTO(undefined, this.queryClient, this.formulaService, this.slugMapService);
        this.#name = t('widgets::scanner::name');
        this.#description = '';
        this.#widgetName.value = t('widgets::scanner::name');

        this.useIdea(
            IdeaDTO.fromIdeaAndPlan(
                {
                    name: this.#name,
                    key: 'ticker',
                    description: this.#description,
                    defaultUniverseId: null,
                    defaultColumnTemplateId: null,
                    isTemplate: false,
                },
                this.#plan,
            ) as IdeaDTO<ScannerPlanDTO>,
        );

        this.#initNewScanner();
    }

    constructor(
        @inject('ImportsManagerModel') @reacts public importsManager: ReactBindings['ImportsManagerModel'],
        @inject('ScannerWidgetAdapter') private adapter: ScannerWidgetAdapter,
        @inject('QueryClient') private queryClient: ReactBindings['QueryClient'],
        @inject('FormulaService') private formulaService: ReactBindings['FormulaService'],
        @inject('ChannelSlice') channelSlice: ChannelSlice,
        @inject('WidgetTabSlice') widgetTabSlice: WidgetTabSlice,
        @inject('FastIdeasSelector') fastIdeasSelector: FastIdeasSelector,
        @inject('WidgetDataModel') @reacts private widgetData: ReactBindings['WidgetDataModel'],
        @inject('Store') private store: ReactBindings['Store'],
        @inject('Logger') logger: ReactBindings['Logger'],
        @inject('SlowIdeasSelector') slowIdeasSelector: SlowIdeasSelector,
        @inject('StickyFilterSlice') stickyFilterSlice: StickyFilterSlice,
        @inject('ColumnOptionsSlice') columnOptionsSlice: ColumnOptionsSlice,
        @inject('ResultsAreaSlice') private resultsAreaSlice: ResultsAreaSlice,
        @inject('TableModel') private totalTableModel: ReactBindings['TableModel'],
        @inject('FeatureFlagService') private featureFlagService: ReactBindings['FeatureFlagService'],
        @inject('SlugMapService') private slugMapService: ReactBindings['SlugMapService'],
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
        @inject('TabId') @optional() tabId?: string,
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);
        this.#log = logger.getSubLogger({ name: 'ScannerWidgetModel' });
        this.#plan = new ScannerPlanDTO(undefined, queryClient, formulaService, slugMapService);
        this.#name = t('widgets::scanner::name');
        this.#description = '';
        this.#widgetName.value = t('widgets::scanner::name');

        this.#channelSlice = channelSlice;
        this.#widgetTabSlice = widgetTabSlice;
        this.#fastIdeasSelector = fastIdeasSelector;
        this.#slowIdeasSelector = slowIdeasSelector;
        this.#stickyFilterSlice = stickyFilterSlice;
        this.#columnOptionsSlice = columnOptionsSlice;

        if (tabId && workspaceService) {
            workspaceService.registerWidget(tabId, this);
            this.disposableStack.defer(() => workspaceService.deregisterWidget(tabId));
        }

        this.#initTotalCount();
        this.eventBus = this.#buildEventBus();
    }

    get widget() {
        return this.widgetData.widget as ScannerWidgetViewModel;
    }

    #hasChangesSinceLastSubmit = false;

    async init(tabId?: string) {
        const baseElements = tabId
            ? COMMON_MENU_HEADER_ELEMENTS
            : STANDALONE_COMMON_MENU_HEADER_ELEMENTS.filter((el) => el.type !== 'channelPicker');
        this.headerElements = makeHeaderElements<ScannerWidgetModel['eventBus']>([
            ...baseElements,
            {
                type: 'ideasMenuItems',
                position: 2,
                overflowOnly: true,
                items: ['new', 'open', 'save', 'copy', 'delete'],
                ideaType: 'fast',
                ideaPlanTypes: ['scanner'],
                selectType: 'single',
                showSelected: false,
                allowDelete: true,
            },
            {
                type: 'sectionViewMenu',
                position: 80,
                priority: 6,
                viewConfig: [
                    { id: 'builder', label: 'Builder' },
                    { id: 'results', label: 'Results' },
                ],
            },
        ]);

        // Initialize slices with shared adapter (works for both workspace and standalone)
        this.#stickyFilterSlice.init(this.adapter);
        this.#columnOptionsSlice.init(this.adapter);
        this.disposableStack.use(this.#stickyFilterSlice);
        this.disposableStack.use(this.#columnOptionsSlice);

        // Initialize FastIdeasSelector with lens (Scanner stores a single idea, not an array of IDs)
        this.#fastIdeasSelector.init(this.adapter, {
            get: (state) => (state.idea?.id ? [state.idea.id] : []),
            set: (_ideaIds) => ({}), // Scanner persists the full idea via useIdea(), not just the ID
            onIdeasSelected: (payload) => {
                const idea = payload.ideas[0];
                if (idea && 'idea' in idea) {
                    this.useIdea(idea as IdeaDTO<ScannerPlanDTO>);
                }
            },
        });
        this.disposableStack.use(this.#fastIdeasSelector);

        // Initialize SlowIdeasSelector with shared adapter
        this.#slowIdeasSelector.init(this.adapter);
        this.disposableStack.use(this.#slowIdeasSelector);

        if (tabId) {
            this.widgetData.init(tabId);

            this.#channelSlice.init(tabId);
            this.#widgetTabSlice.init(tabId);
            this.disposableStack.use(this.#channelSlice);
            this.disposableStack.use(this.#widgetTabSlice);

            this.#sectionVisibility.value = {
                builder: this.widget.displayingForm,
                results: this.widget.displayingResults,
            };

            // Subscribe to section visibility changes from store
            const unsubscribe = this.store
                .subscribeSelector((state) => {
                    const tabExists = state.tab.byId[tabId] !== undefined;
                    if (!tabExists) {
                        return null;
                    }
                    const widget = state.tab.byId[tabId]?.widget as ScannerWidgetViewModel | undefined;
                    return widget
                        ? {
                              displayingForm: widget.displayingForm,
                              displayingResults: widget.displayingResults,
                          }
                        : null;
                })
                .handle((widgetData) => {
                    if (widgetData !== null) {
                        this.#sectionVisibility.value = {
                            builder: widgetData.displayingForm,
                            results: widgetData.displayingResults,
                        };
                    }
                });

            this.disposableStack.defer(() => {
                unsubscribe();
            });
        } else {
            // Standalone mode: initialize signal from adapter state (e.g. URL params)
            this.#sectionVisibility.value = {
                builder: this.adapter.getState().isShowingBuilder,
                results: this.adapter.getState().isShowingResults,
            };
        }

        const uniqueMountId = v4();
        const userViewKeyUnsubscribe = this.store
            .subscribeSelector((state) => useWidgetTabIdForUserViewKey.select(state), { leading: true })
            .handle((useTabId) => {
                this.#userViewKey.value = useTabId && tabId !== undefined ? tabId : uniqueMountId;
            });
        this.disposableStack.defer(userViewKeyUnsubscribe);

        // Initialize ResultsAreaSlice
        this.resultsAreaSlice.init(this.adapter, {
            columnTemplateKey: 'ticker',
            tabId,
            ideaMode: 'scanner',
            ideaUserViewKeyScope: 'fast',
            userViewKeySignal: this.#userViewKey,
            validationModeSignal: this.#isValidationModeEnabled,
            slowIdeasSelector: this.#slowIdeasSelector,
            onUserSelectedSymbol: (symbol) => this.adapter.userSelectedSymbol(symbol),
            stickyFilterEventBus: this.#stickyFilterSlice.eventBus,
            columnOptionsEventBus: this.#columnOptionsSlice.eventBus,
            devtoolsName: 'Scanner/results',
        });
        this.disposableStack.use(this.resultsAreaSlice);

        // Sync resolved column template from slice to local signal
        this.disposableStack.defer(
            effect(() => {
                const loadedTemplate = this.resultsAreaSlice.columnTemplate.value;
                if (loadedTemplate && this.#selectedColumnTemplate.value !== loadedTemplate) {
                    this.#selectedColumnTemplate.value = loadedTemplate;
                }
            }),
        );

        // Wire table connection status and row count from slice
        this.disposableStack.defer(
            effect(() => {
                const table = this.resultsAreaSlice.table;
                this.#connectionStatus.value = {
                    status: table.connectionStatus.value,
                    progress: table.progress.value,
                    failureCount: table.failureCount.value,
                };
            }),
        );
        this.disposableStack.use(
            this.resultsAreaSlice.table.count$.subscribe((count) => {
                this.#tableRowCount.value = count;
            }),
        );

        this.#refetchHandler = () => this.resultsAreaSlice.table.refetch();

        // Update resultsAreaSlice with the widget plan idea whenever lastSubmit changes
        this.disposableStack.defer(
            effect(() => {
                void this.#lastSubmit.value;
                untracked(() => this.resultsAreaSlice.setWidgetPlanIdea(this.#plan.toTempIdea()));
            }),
        );

        // Initialize column template signal from adapter state
        this.#selectedColumnTemplate.value = this.adapter.getState().columnTemplate;

        // if we have an idea, set up the plan from it
        if (this.idea) {
            this.#name = this.idea.name ?? '';
            this.#description = this.idea.description ?? '';
            this.#widgetName.value =
                this.#name ||
                (tabId ? this.#widgetTabSlice.eventBus.getSignal('widget::name').value : t('widgets::scanner::name'));

            if (this.idea.plan.type === 'scanner') {
                this.#plan = await ScannerPlanDTO.fromPlan(
                    this.idea.plan,
                    this.queryClient,
                    this.formulaService,
                    this.slugMapService,
                    {
                        isDirty: this.adapter.getState().isIdeaDirty ?? false,
                    },
                );
                this.#onScannerChange();
            }
        }

        this.#initNewScanner();

        // If the widget was created with defaultIdeaStatus: 'loading', kick off async
        // default idea loading (fire-and-forget)
        if (tabId && this.widget.defaultIdeaStatus === 'loading') {
            this.#loadDefaultIdea();
        }
    }

    async #loadDefaultIdea() {
        try {
            const config = await this.queryClient.fetchQuery(getWorkspaceConfigQuery());

            // Apply default universe immediately
            this.#updateWidgetState({ universeId: config.defaultUniverseId });

            const defaultScannerPlanId = config.defaultScannerPlanId;
            if (!defaultScannerPlanId) {
                this.#updateWidgetState({ defaultIdeaStatus: 'failed' });
                return;
            }

            // Fetch the idea with a timeout
            const loadedIdea = await Promise.race([
                this.queryClient.fetchQuery(
                    getIdeaByIdQuery<ConcreteIdea & { plan: ScannerPlan }>(defaultScannerPlanId),
                ),
                new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Timeout')), 10_000)),
            ]);

            if (!loadedIdea) {
                this.#updateWidgetState({ defaultIdeaStatus: 'failed' });
                return;
            }

            const planDto = await ScannerPlanDTO.fromPlan(
                loadedIdea.plan,
                this.queryClient,
                this.formulaService,
                this.slugMapService,
            );
            const { plan: _plan, ...ideaDescriptor } = loadedIdea;
            const ideaDto = new IdeaDTO<ScannerPlanDTO>({ idea: ideaDescriptor, plan: planDto });

            await this.useIdea(ideaDto);
            this.#updateWidgetState({ defaultIdeaStatus: 'loaded' });
        } catch {
            this.#updateWidgetState({ defaultIdeaStatus: 'failed' });
        }
    }

    #initTotalCount() {
        this.totalTableModel.init({
            queryOptions: getTotalRowsCountQuery(this.adapter.getState().universeId ?? undefined),
            devtoolsName: 'Scanner/totalCount',
        });

        this.disposableStack.use(
            this.totalTableModel.count$.subscribe((count) => {
                this.rowCountTotal = count;
                this.#rowCountTotalSignal.value = count;
            }),
        );

        // Reinit total count when universeId changes
        this.disposableStack.defer(
            this.adapter.subscribe(() => {
                const universeId = this.adapter.getState().universeId;
                this.totalTableModel.updateQueryOptions(getTotalRowsCountQuery(universeId ?? undefined));
            }),
        );
    }

    #initNewScanner() {
        // trigger a submit, so the results view loads any time a new scanner is used
        this.#lastSubmit.value = Instant.now();

        // Clean up previous plan's listener before adding new one
        // (this method can be called multiple times via useIdea/createNewScanner)
        this.#planListenerCleanup?.();

        // Capture plan reference to ensure cleanup removes listener from correct plan
        const plan = this.#plan;

        // set up event bindings
        const listener = this.#onScannerChange.bind(this);
        plan.changeTarget.addEventListener(SCRANNER_CHANGE_EVENT, listener);

        // Store cleanup function for this plan's listener
        this.#planListenerCleanup = () => {
            plan.changeTarget.removeEventListener(SCRANNER_CHANGE_EVENT, listener);
        };

        // Register with disposableStack for final cleanup when widget is destroyed
        this.disposableStack.defer(() => {
            this.#planListenerCleanup?.();
            this.#planListenerCleanup = null;
        });

        plan.changeTarget.dispatchEvent(new Event(SCRANNER_CHANGE_EVENT));
    }

    eventBus!: ScannerWidgetModel['eventBus'];

    #buildEventBus(): ScannerWidgetModel['eventBus'] {
        return MergedEventBus.from(
            EventBusBuilder.create()
                .addEvent('widget::selectSlowIdea', async (payload) => {
                    const idea = payload.ideas[0];
                    if (!idea) {
                        this.universeId = null;
                    } else if ('universeId' in idea) {
                        this.universeId = idea.universeId;
                    } else if ('idea' in idea) {
                        this.universeId = idea.idea.id ?? null;
                    }
                })
                .addEvent('widget::toggleSectionVisibility', (payload) => {
                    if (payload.sectionId === 'builder') {
                        this.isShowingBuilder = payload.show;
                    } else if (payload.sectionId === 'results') {
                        this.isShowingResults = payload.show;
                    }
                    this.#sectionVisibility.value = {
                        builder: this.isShowingBuilder,
                        results: this.isShowingResults,
                    };
                })
                // IPC-safe event handlers for idea editor actions
                // NOTE: saveIdea/saveIdeaAs/createNewScanner are called directly by event handlers.
                // Errors are caught here to prevent unhandled promise rejections since EventBus.emit() doesn't await.
                // TODO: Consider adding error notification via signals for proper UI feedback.
                .addEvent('widget::ideaEditorSave', async () => {
                    try {
                        await this.saveIdea();
                    } catch (e) {
                        this.#log.error({ message: 'Failed to save idea', error: e as Error });
                    }
                })
                .addEvent('widget::ideaEditorSaveAs', async (payload) => {
                    try {
                        await this.saveIdeaAs(payload.name);
                    } catch (e) {
                        this.#log.error({ message: 'Failed to save idea as', error: e as Error });
                    }
                })
                .addEvent('widget::ideaEditorCreateNew', () => {
                    this.createNewScanner();
                })
                // Column template events
                .addEvent('widget::selectColumnTemplate', (payload) => {
                    this.adapter.persist({ columnTemplate: payload.templateId });
                    this.#updateWidgetState({ columnTemplate: payload.templateId });
                    this.#selectedColumnTemplate.value = payload.templateId;
                })
                .addEvent('widget::updateConcreteTemplate', (payload) => {
                    this.adapter.persist({ columnTemplate: payload.columnTemplate });
                    this.#selectedColumnTemplate.value = payload.columnTemplate;
                })
                // Column template library event
                .addEvent('widget::onColumnTemplateLibraryOpen', (payload) => {
                    this.#isColumnTemplateLibraryOpen.value = payload.isOpen;
                })
                // Validation mode event
                .addEvent('widget::toggleValidationMode', () => {
                    this.#isValidationModeEnabled.value = !this.#isValidationModeEnabled.value;
                })
                // Export CSV event
                .addEvent('widget::exportCSV', () => {
                    void this.#exportCSV();
                })
                // Signals
                .addSignal('widget::ideaEditorIsDirty', () => this.#isDirty)
                .addSignal('widget::ideaEditorCanSave', () => computed(() => this.canSaveIdea))
                .addSignal('widget::ideaEditorIsTemplate', () => computed(() => this.idea?.isTemplate ?? false))
                .addSignal('widget::ideaEditorUnsavedChangesLabel', () => computed(() => 'Unsaved Changes'))
                .addSignal('widget::sectionVisibility', () => this.#sectionVisibility)
                // Column template signals
                .addSignal('widget::selectedColumnTemplate', () => this.#selectedColumnTemplate)
                .addEvent('widget::openGridSettingsDialog', (payload) => {
                    this.#isGridSettingsDialogOpen.value = payload.isOpen;
                    this.#gridSettingsDialogTarget.value =
                        payload.isOpen && (payload.targetTab !== undefined || payload.targetColumnId !== undefined)
                            ? { tab: payload.targetTab, columnId: payload.targetColumnId }
                            : null;
                })
                .addSignal('widget::isGridSettingsDialogOpen', () => this.#isGridSettingsDialogOpen)
                .addSignal('widget::gridSettingsDialogTarget', () => this.#gridSettingsDialogTarget)
                .addSignal('widget::isColumnTemplateLibraryOpen', () => this.#isColumnTemplateLibraryOpen)
                .addSignal('widget::formattableGridSettings', () =>
                    computed(() => {
                        const columnTemplate = this.#selectedColumnTemplate.value;
                        if (!columnTemplate || typeof columnTemplate === 'string') {
                            return { settings: null, defaults: null };
                        }
                        const sanitized = generateWorkableColumnTemplateGridSettings(columnTemplate);
                        return { settings: sanitized, defaults: sanitized };
                    }),
                )
                // Validation signal
                .addSignal('widget::isValidationModeEnabled', () => this.#isValidationModeEnabled)
                // Table row count signals
                .addSignal('widget::tableRowCount', () => this.#tableRowCount)
                .addSignal('widget::tableTotalRowCount', () => this.#rowCountTotalSignal)
                // Connection status signal
                .addSignal('widget::connectionStatus', () => this.#connectionStatus)
                .addEvent('widget::connectionStatusRefetch', () => {
                    this.#refetchHandler?.();
                })
                .build(),
        )
            .merge(this.#channelSlice.eventBus)
            .merge(this.#widgetTabSlice.eventBus)
            .merge(this.#fastIdeasSelector.eventBus)
            .merge(this.#slowIdeasSelector.eventBus)
            .merge(this.#stickyFilterSlice.eventBus)
            .merge(this.#columnOptionsSlice.eventBus)
            .merge(this.resultsAreaSlice.eventBus)
            .merge(this.resultsAreaSlice.columnFilters.eventBus)
            .merge(
                // Override widget::name to show idea name instead of tab name
                EventBusBuilder.create()
                    .addSignal('widget::name', () => this.#widgetName)
                    .build(),
            );
    }

    get isShowingBuilder() {
        return this.adapter.getState().isShowingBuilder;
    }

    set isShowingBuilder(value: boolean) {
        this.adapter.persist({ isShowingBuilder: value });
    }

    get isShowingResults() {
        return this.adapter.getState().isShowingResults;
    }

    set isShowingResults(value: boolean) {
        this.adapter.persist({ isShowingResults: value });
    }

    #lastSubmit: Signal<Instant> = signal(Instant.now());

    #rowCountFiltered = 0;
    get rowCountFiltered() {
        return this.#rowCountFiltered;
    }

    @reacts set rowCountFiltered(value: number) {
        this.#rowCountFiltered = value;
    }

    #rowCountTotal = 0;
    get rowCountTotal() {
        return this.#rowCountTotal;
    }

    @reacts set rowCountTotal(value: number) {
        this.#rowCountTotal = value;
    }

    #onScannerChange() {
        // TODO add back the row count, copy mostly from ScreenerWidgetModel

        // this will be called each time the scanner changes, including when we first load it.
        // so set hasChangesSinceLastSubmit to true if the scanner is dirty
        this.#hasChangesSinceLastSubmit = this.#plan.isDirty;

        // Update the isDirty signal for reactive UI updates
        this.#isDirty.value = this.#plan.isDirty;

        // Sync dirty state to Redux so workspace save can serialize by reference when clean
        this.adapter.persist({ isIdeaDirty: this.#plan.isDirty });

        this.rerender();
    }

    async saveIdea(): Promise<ConcreteIdea> {
        const canPut = (_idea: Idea | NewIdea): _idea is ConcreteIdea => {
            return Boolean(this.idea.id);
        };

        const contractIdea = this.toIdea();

        const ideaToSave = canPut(contractIdea) ? contractIdea : null;

        if (!ideaToSave) {
            throw new Error('Cannot save idea without an id');
        }

        const newIdea = (await updateIdea(ideaToSave)) as ConcreteIdea & {
            plan: ScannerPlan;
        };

        invalidateIdeaRecordsQueries(this.queryClient);

        this.useIdea(
            IdeaDTO.fromIdeaAndPlan(
                newIdea,
                await ScannerPlanDTO.fromPlan(newIdea.plan, this.queryClient, this.formulaService, this.slugMapService),
            ) as IdeaDTO<ScannerPlanDTO>,
        );

        return newIdea;
    }

    async saveIdeaAs(name: string): Promise<ConcreteIdea> {
        const ideaToSave = this.toIdea(name);

        if (!ideaToSave.name) {
            throw new Error('Cannot save idea without a name');
        }

        const newIdea = (await createIdea(ideaToSave)) as ConcreteIdea & {
            plan: ScannerPlan;
        };

        invalidateIdeaRecordsQueries(this.queryClient);

        this.useIdea(
            IdeaDTO.fromIdeaAndPlan(
                newIdea,
                await ScannerPlanDTO.fromPlan(newIdea.plan, this.queryClient, this.formulaService, this.slugMapService),
            ) as IdeaDTO<ScannerPlanDTO>,
        );

        return newIdea;
    }

    toIdea(name?: string): NewIdea {
        return {
            ...this.idea,
            name: name ?? this.#name,
            description: this.#description,
            plan: this.#plan.toContract(),
        };
    }

    #plan: ScannerPlanDTO;

    get scanner() {
        return this.#plan;
    }

    get splitPercentage() {
        return this.adapter.getState().splitPercentage;
    }

    set splitPercentage(value: number) {
        this.adapter.persist({ splitPercentage: value });
    }

    get table() {
        return this.resultsAreaSlice.table;
    }

    get stitching() {
        return this.resultsAreaSlice.stitching;
    }

    get columnFilterState() {
        return this.resultsAreaSlice.columnFilterState;
    }

    get columnFilters() {
        return this.resultsAreaSlice.columnFilters;
    }

    get canSubmit() {
        return (
            this.#hasChangesSinceLastSubmit &&
            this.#plan.conditions.every((condition) => {
                if (!condition.enabled) return true;

                if (condition.type === 'formula') {
                    return Boolean(condition.formula);
                }

                if (
                    !(
                        Boolean(condition.selection) ||
                        condition.indicator.params.every((x) => x.defaultValue !== undefined || x.optional)
                    )
                ) {
                    return false;
                }

                switch (condition.type) {
                    case 'quantitative':
                        if (!condition.max && !condition.min) return false;

                        const invalidNumbers =
                            condition.max &&
                            condition.min &&
                            (condition.min.value > condition.max.value ||
                                (condition.min.mode === 'exclusive' &&
                                    condition.max?.mode === 'exclusive' &&
                                    condition.min.value === condition.max.value));

                        if (invalidNumbers) return false;

                        return true;
                    case 'membership':
                    case 'equality':
                        return true;
                }
            })
        );
    }

    submit() {
        this.#lastSubmit.value = Instant.now();
        this.#hasChangesSinceLastSubmit = false;
    }

    userSelectedSymbol(symbol: string) {
        this.adapter.userSelectedSymbol(symbol);
    }

    get idea() {
        return this.adapter.getState().idea;
    }

    get universeId() {
        return this.adapter.getState().universeId;
    }

    set universeId(value: string | null) {
        this.adapter.persist({ universeId: value });
    }

    async useIdea(idea: IdeaDTO<ScannerPlanDTO>) {
        this.adapter.persist({
            idea: {
                ...idea.idea,
                plan: idea.plan.toContract(),
            } as Idea & { plan: ScannerPlan },
        });

        this.#name = idea.name ?? '';
        this.#description = idea.idea.description ?? '';
        this.#widgetName.value =
            this.#name ||
            (this.widgetData.tabId
                ? this.#widgetTabSlice.eventBus.getSignal('widget::name').value
                : t('widgets::scanner::name'));

        this.#plan = idea.plan;
        this.#initNewScanner();
    }

    #updateWidgetState(updates: Partial<ScannerWidgetViewModel>) {
        this.store.dispatch(
            updateWidget({
                tabId: this.widgetData.tabId,
                widgetUpdates: updates,
            }),
        );
    }

    async #exportCSV(): Promise<void> {
        const columnTemplate = this.resultsAreaSlice.columnTemplate.value;
        if (!columnTemplate || typeof columnTemplate === 'string') {
            return;
        }

        const validationColumns = this.resultsAreaSlice.eventBus.getSignal('widget::validationColumns').value;

        const columnNames = [
            ...columnTemplate.columns.map((col) => ({
                id: col.id,
                name: col.name,
            })),
            ...validationColumns.map((column) => ({
                id: column.id,
                name: getValidationColumnDisplayName(column),
            })),
        ].reduce(
            (names, { id, name }) => {
                names[id] = name;
                return names;
            },
            {} as Record<string, string>,
        );

        await this.resultsAreaSlice.exportCSV(columnNames);
    }
}
